/**
 * DetailReviewStep — review DetailedEntity[] from wave 2.
 * Shows progress during extraction and full cards after completion.
 * FR-019, FR-020, FR-021
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { EntityReviewPanel, CAMPAIGN_TYPE_LABELS } from '../homebrew/EntityReviewPanel.js';
import type { ReviewableEntity } from '../homebrew/EntityReviewPanel.js';
import type { DetailedEntity } from '@roll1/shared';

interface DetailReviewStepProps {
  details: DetailedEntity[];
  progress?: { current: number; total: number };
  isLoading: boolean;
  onConfirm: (details: DetailedEntity[]) => void;
  onCancel: () => void;
}

function detailToReviewable(detail: DetailedEntity): ReviewableEntity {
  const data: Record<string, unknown> = {};

  switch (detail.entityType) {
    case 'npc':
      data.race = detail.race;
      data.background = detail.background;
      data.classes = detail.classes.map(c => `${c.class} Lv.${c.level}`).join(', ');
      data.hp = `${detail.hp.current}/${detail.hp.max}`;
      data.ac = detail.ac;
      if (detail.description) data.description = detail.description;
      break;
    case 'location':
      data.locationType = detail.locationType;
      data.description = detail.description;
      data.relatedNpcs = detail.relatedNpcNames.join(', ');
      break;
    case 'note':
      data.category = detail.category;
      data.content = detail.content.slice(0, 200);
      break;
    case 'journal':
      data.category = detail.category;
      data.content = detail.content.slice(0, 200);
      break;
    case 'encounter':
      data.difficulty = detail.difficulty;
      data.monsters = detail.monsters.map(m => `${m.name} x${m.count}`).join(', ');
      data.environment = detail.environment;
      break;
  }

  return {
    type: detail.entityType,
    slug: detail.slug,
    name: detail.name,
    data,
    sourceText: '',
    confidence: detail.confidence,
    selected: true,
  };
}

export function DetailReviewStep({
  details,
  progress,
  isLoading,
  onConfirm,
  onCancel,
}: DetailReviewStepProps) {
  const [entities, setEntities] = useState<ReviewableEntity[]>(() =>
    details.map(detailToReviewable),
  );

  // Sync entities when details change (streaming)
  React.useEffect(() => {
    setEntities(details.map(detailToReviewable));
  }, [details]);

  const handleToggle = useCallback((index: number) => {
    setEntities(prev => prev.map((e, i) =>
      i === index ? { ...e, selected: !e.selected } : e,
    ));
  }, []);

  const handleUpdate = useCallback((index: number, changes: Partial<ReviewableEntity>) => {
    setEntities(prev => prev.map((e, i) =>
      i === index ? { ...e, ...changes } : e,
    ));
  }, []);

  const handleSelectAll = useCallback(() => {
    setEntities(prev => prev.map(e => ({ ...e, selected: true })));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setEntities(prev => prev.map(e => ({ ...e, selected: false })));
  }, []);

  const selectedCount = entities.filter(e => e.selected).length;

  const handleConfirm = () => {
    const selectedSlugs = new Set(
      entities.filter(e => e.selected).map(e => e.slug),
    );
    const selectedDetails = details.filter(d => selectedSlugs.has(d.slug));
    onConfirm(selectedDetails);
  };

  return (
    <div className="space-y-4">
      {/* [STYLE_TODO] Progress bar and status */}
      {isLoading && progress && (
        <div className="space-y-1">
          <p className="text-ui font-medium">
            Детализация: {progress.current}/{progress.total}
          </p>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary rounded-full h-2 transition-all"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {!isLoading && (
        <p className="text-ui font-medium">
          Детализировано: {details.length} сущностей
        </p>
      )}

      <div className="max-h-[400px] overflow-auto">
        <EntityReviewPanel
          entities={entities}
          onToggle={handleToggle}
          onUpdate={handleUpdate}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          typeLabels={CAMPAIGN_TYPE_LABELS}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Отмена</Button>
        <Button disabled={isLoading || selectedCount === 0} onClick={handleConfirm}>
          Извлечь связи ({selectedCount})
        </Button>
      </div>
    </div>
  );
}
